<template>
    <div class="item-information">
        <div v-if="sections.language" class="d-flex justify-content-center">
            <img class="content-flag" :src="getFlagImage()" />
        </div>
        <div v-if="sections.status" class="card section">
            <div class="card-body">
                <div class="row">
                    <h5 class="col-12 text-uppercase bg-light text-center py-1">
                        Status
                    </h5>
                    <div class="col-12">
                        <div class="d-flex flex-column mb-3">
                            <div
                                v-if="item.createdAt"
                                class="d-flex flex-wrap my-2"
                            >
                                <div class="mr-1">
                                    <b>Created at: </b>
                                </div>
                                <div>
                                    {{ item.createdAt | formatDate }}
                                </div>
                            </div>
                            <div
                                v-if="item.updatedAt"
                                class="d-flex flex-wrap my-2"
                            >
                                <div class="mr-1">
                                    <b>Last update at:</b>
                                </div>
                                <div>
                                    {{ item.updatedAt | formatDate }}
                                </div>
                            </div>
                            <div
                                v-if="item.createdBy"
                                class="d-flex flex-wrap my-2"
                            >
                                <div class="mr-1">
                                    <b>Created by:</b>
                                </div>
                                {{ item.createdBy.fullName }}
                                <router-link
                                    v-if="item.createdBy.id"
                                    :to="`/users/${item.createdBy.id}/edit`"
                                    class="ml-2"
                                >
                                    <i class="fas fa-external-link-alt" />
                                </router-link>
                            </div>
                        </div>
                        <div
                            v-if="componentOptions.deleteButton"
                            class="col-12 d-flex justify-content-center"
                        >
                            <button
                                v-b-modal.confirm-delete-modal
                                type="button"
                                class="btn btn-danger btn-static waves-effect waves-light mb-4"
                            >
                                <i class="far fa-trash-alt" />
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="sections.customAuthor"
            class="card section"
            :class="dynamicClass"
        >
            <div class="card-body">
                <div class="row">
                    <h5 class="col-12 text-uppercase bg-light text-center py-1">
                        Custom author name
                    </h5>
                    <div class="form-group col-12">
                        <div class="input-group">
                            <input
                                :value="customAuthorName"
                                type="text"
                                class="form-control"
                                placeholder="Custom author name"
                                @blur="onCustomAuthorNameBlur"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="sections.permalink"
            class="card section"
            :class="dynamicClass"
        >
            <div class="card-body">
                <div class="row">
                    <h5 class="col-12 text-uppercase bg-light text-center py-1">
                        Permalink
                    </h5>
                    <div class="form-group col-12">
                        <label for="slug">URL Slug</label>
                        <div class="input-group">
                            <input
                                id="slug"
                                v-model="newSlug"
                                type="text"
                                class="form-control"
                                :class="{
                                    'is-invalid': slugError
                                }"
                                placeholder="URL slug"
                                @blur="onSlugBlur"
                            />
                            <div class="invalid-feedback">
                                {{ slugError }}
                            </div>
                        </div>
                        <div class="mt-2">
                            {{ slugUrl }}
                        </div>

                        <div v-if="editMode" class="mt-2 text-center">
                            <hr />

                            <i class="fas fa-exclamation-triangle fa-lg" />

                            <br />

                            <small>
                                Changing <strong>permalink</strong> slug might
                                corrupt links that reference it in other
                                sections of CMS.
                            </small>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="sections.featuredImage" class="card section">
            <div class="card-body">
                <div class="row">
                    <h5 class="col-12 text-uppercase bg-light text-center py-1">
                        Featured Image
                    </h5>
                    <div class="col-12">
                        <div class="form-group">
                            <media-chooser
                                v-model="image"
                                type="image"
                                id-only
                            />
                        </div>
                    </div>
                    <div v-if="sections.imageAuthor" class="col-12">
                        <div class="form-group">
                            <label for="imageAuthor">Image Author</label>
                            <input
                                :value="imageAuthor"
                                :class="dynamicClass"
                                type="text"
                                class="form-control"
                                placeholder="Image author name"
                                @blur="onImageAuthorBlur"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <select-section
            v-if="sections.categories"
            show-edit-button
            label="name"
            :value="categories"
            :class="dynamicClass"
            title="Categories"
            multiple
            :filters="baseFilters"
            :get-function-name="getCategoriesFunctionName"
            @input="onCategoriesChange"
            @edit-option="({ id }) => onEditOption(id, 'article-categories')"
        />

        <select-section
            v-if="sections.medicationCategories"
            show-edit-button
            label="name"
            :value="medicationCategories"
            :class="dynamicClass"
            title="Drugs"
            multiple
            :filters="baseFilters"
            :get-function-name="getMedicationCategoriesFunctionName"
            @input="onMedicationCategoriesChange"
            @edit-option="({ id }) => onEditOption(id, 'medication-categories')"
        />

        <select-section
            v-if="sections.branch"
            show-edit-button
            label="title"
            :value="branch"
            :class="dynamicClass"
            title="Branch"
            :filters="statusFilters"
            :get-function-name="getBranchesFunctionName"
            @input="onBranchChange"
            @edit-option="({ id }) => onEditOption(id, 'branches')"
        />

        <select-section
            v-if="sections.tags"
            :value="tags"
            :class="dynamicClass"
            title="Tags"
            multiple
            :get-function-name="getTagsFunctionName"
            @input="onTagsChange"
        />

        <select-section
            v-if="sections.contents"
            label="name"
            :value="contents"
            :class="dynamicClass"
            title="Pages"
            multiple
            :filters="pageFilters"
            :get-function-name="getContentsFunctionName"
            @input="onContentsChange"
        />

        <confirmation-modal
            id="confirm-delete-modal"
            :content="'Do you want to delete this item?'"
            @agree="$emit('on-delete-confirm')"
        />
    </div>
</template>

<script>
import slugify from 'slugify';
import { mapGetters } from 'vuex';

import { frontendUrl } from '@/config';
import SelectSection from './SelectSection';
import getCountryFlag from '@/helpers/getCountryFlag';
import ConfirmationModal from '@/components/elements/ConfirmationModal';

export default {
    components: {
        SelectSection,
        ConfirmationModal
    },

    props: {
        sections: {
            type: Object,
            required: false,
            default: () => ({
                status: false,
                permalink: false,
                featuredImage: false,
                imageAuthor: false,
                categories: false,
                medicationCategories: false,
                branch: false,
                customAuthor: false,
                language: false,
                contents: false
            })
        },
        slug: {
            type: String,
            required: false,
            default: ''
        },
        slugError: {
            type: String,
            required: false,
            default: ''
        },
        item: {
            type: Object,
            required: false,
            default: () => ({})
        },
        featuredImage: {
            type: String,
            required: false,
            default: ''
        },
        editMode: {
            type: Boolean,
            required: false,
            default: false
        },
        options: {
            type: Object,
            required: false,
            default: () => ({})
        },
        getCategoriesFunctionName: {
            type: String,
            required: false,
            default: ''
        },
        categories: {
            type: Array,
            required: false,
            default: null
        },
        getMedicationCategoriesFunctionName: {
            type: String,
            required: false,
            default: ''
        },
        medicationCategories: {
            type: Array,
            required: false,
            default: null
        },
        getBranchesFunctionName: {
            type: String,
            required: false,
            default: ''
        },
        branch: {
            type: Object,
            required: false,
            default: null
        },
        getTagsFunctionName: {
            type: String,
            required: false,
            default: ''
        },
        tags: {
            type: Array,
            required: false,
            default: () => []
        },
        customAuthorName: {
            type: String,
            required: false,
            default: ''
        },
        imageAuthor: {
            type: String,
            required: false,
            default: ''
        },
        language: {
            type: Object,
            required: false,
            default: () => ({})
        },
        contents: {
            type: Array,
            required: false,
            default: null
        },
        getContentsFunctionName: {
            type: String,
            required: false,
            default: ''
        }
    },

    data() {
        return {
            newSlug: null,
            image: '',
            defaultOptions: {
                featuredImagePath: '',
                deleteButton: true,
                slugBasicUrl: ''
            }
        };
    },

    computed: {
        ...mapGetters({
            selectedLanguage: 'languages/selectedLanguage'
        }),

        dynamicClass() {
            return this.selectedLanguage.code === 'he' ? 'rtl' : '';
        },

        slugUrl() {
            const { slugBasicUrl } = this.componentOptions;

            if (!slugBasicUrl) {
                return `${frontendUrl}/${this.newSlug || '_______'}`;
            }

            return `${frontendUrl}/${slugBasicUrl}/${
                this.newSlug || '_______'
            }`;
        },
        componentOptions() {
            return { ...this.defaultOptions, ...this.options };
        },

        baseFilters() {
            return {
                'language.code': {
                    value: [this.selectedLanguage.code],
                    type: 'eq'
                }
            };
        },

        statusFilters() {
            return {
                ...this.baseFilters,
                status: { value: 'published', type: 'eq' }
            };
        },

        pageFilters() {
            return {
                ...this.baseFilters,
                type: { value: 'page', type: 'eq' }
            };
        }
    },

    watch: {
        image() {
            this.$emit('on-featured-image-change', this.image);
        },
        slug: {
            handler() {
                this.newSlug = this.slug;

                this.onSlugBlur();
            },
            immediate: true
        }
    },

    async created() {
        if (this.featuredImage) {
            this.image = this.featuredImage;
        }
    },

    methods: {
        onSlugBlur() {
            if (this.newSlug) {
                this.newSlug = this.newSlug.toLowerCase();

                this.newSlug = slugify(this.newSlug, {
                    lower: true,
                    strict: true
                });
            }

            this.$emit('on-slug-change', this.newSlug);
        },

        onTagsChange(tags) {
            this.$emit('on-tags-change', tags);
        },

        onCategoriesChange(categories) {
            this.$emit('on-categories-change', categories);
        },

        onMedicationCategoriesChange(medicationCategories) {
            this.$emit('on-medication-categories-change', medicationCategories);
        },

        onBranchChange(branch) {
            this.$emit('on-branch-change', branch);
        },

        onCustomAuthorNameBlur($e) {
            this.$emit('custom-author-name-change', $e.target.value);
        },

        onImageAuthorBlur($e) {
            this.$emit('on-image-author-change', $e.target.value);
        },

        getFlagImage() {
            return getCountryFlag(this.language);
        },

        onEditOption(id, resource) {
            this.$router.push(`/${resource}/${id}/edit`);
        },

        onContentsChange(contents) {
            this.$emit('on-contents-change', contents);
        }
    }
};
</script>
